import React from 'react'
import PropTypes from 'prop-types'
import {
  Backdrop,
  Checkbox,
  CircularProgress,
  Divider,
  Fade,
  Grid,
  IconButton,
  Modal,
  Typography,
} from '@material-ui/core'
import CloudDownload from '@material-ui/icons/CloudDownloadOutlined'
import ZoomInIcon from '@material-ui/icons/ZoomIn'
import ZoomOutIcon from '@material-ui/icons/ZoomOut'
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import DeleteIcon from '@material-ui/icons/DeleteOutline'
import SelectAllIcon from '@material-ui/icons/SelectAll'
import AddPhotoAlternateIcon from '@material-ui/icons/AddPhotoAlternate'
import moment from 'moment'
import clsx from 'clsx'

import { CustomButton } from 'components/CustomButton'
import { ModalConfirmacao } from 'components/ModalConfirmacao'
import GaleriaVazia from './GaleriaVazia'
import useStyles from './Galeria.styles'
import { useGaleria } from './hooks/useGaleria'

export const Galeria = (props) => {
  const classes = useStyles()

  const {
    photos,
    selectedPhotos,
    detailPhoto,
    isDetailOpen,
    zoom,
    initialLoading,
    accessDenied,
    isDeleteModalOpen,
    setIsDeleteModalOpen,
    isMobile,
    deleting,

    inputRef,

    zoomIn,
    zoomOut,
    handleCheck,
    selectPhotoForDetail,
    closePhotoDetail,
    handleTouchStart,
    handleTouchEnd,
    openFileSelector,
    handleSelectFiles,
    handleDownload,
    handleMultipleDownload,
    handleDelete,
    handleSelectAll,
    isSelected,
    updatePhotoState,
    deleteDisabled,
    panPosition,
    isDragging,
    handleMouseDown,
    handleMouseMove,
    handleMouseUpOrLeave,
    handleTouchStartDrag,
    handleTouchMoveDrag,
    handleTouchEndDrag,
  } = useGaleria(props)

  if (accessDenied) {
    return (
      <div
        className={classes.root}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '300px',
          padding: '2rem',
        }}
      >
        <Typography variant="h5" color="error" style={{ marginBottom: 16 }}>
          Acesso Negado
        </Typography>
        <Typography variant="body1">
          Você não possui permissão para visualizar a galeria deste caso.
        </Typography>
      </div>
    )
  }

  return (
    <div className={classes.root}>
      {/* Cabeçalho da Galeria */}
      <div className={classes.headerContainer}>
        <Typography component="h1" variant="h4" className={classes.titulo}>
          Galeria de fotos
        </Typography>
        <div className={classes.botoesHeader}>
          <CustomButton
            colorType="primary"
            variant="outlined"
            onClick={openFileSelector}
            startIcon={<AddPhotoAlternateIcon />}
          >
            Adicionar Foto
          </CustomButton>

          <input
            type="file"
            hidden
            multiple
            ref={inputRef}
            accept="image/*, .jpg, .jpeg, .png, .JPG, .JPEG, .PNG"
            onChange={handleSelectFiles}
          />

          <CustomButton
            colorType="primary"
            variant="outlined"
            onClick={handleSelectAll}
            startIcon={<SelectAllIcon />}
          >
            {isMobile ? 'Selecionar tudo' : 'Selecionar tudo'}
          </CustomButton>
        </div>
      </div>

      <Divider className={classes.dividerHeader} />

      {/* Menu Superior Flutuante de Ações em Lote */}
      {selectedPhotos.length > 0 && (
        <div className={classes.menuSuperior}>
          <IconButton onClick={handleMultipleDownload} title="Baixar selecionadas">
            <CloudDownload fontSize="large" color="primary" />
          </IconButton>
          <Typography variant="body2" style={{ fontWeight: 600 }}>
            Fotos selecionadas: {selectedPhotos.length}
          </Typography>
          <IconButton
            onClick={() => setIsDeleteModalOpen(true)}
            disabled={deleteDisabled || deleting}
            title="Excluir selecionadas"
          >
            <DeleteIcon fontSize="large" color={deleteDisabled || deleting ? 'disabled' : 'error'} />
          </IconButton>
        </div>
      )}

      {/* Grid de Exibição das Fotos */}
      <Grid container spacing={2} className={classes.galeria}>
        {initialLoading ? (
          <div className={classes.carrementoInicial}>
            <CircularProgress size={35} />
          </div>
        ) : photos.length === 0 ? (
          <GaleriaVazia classes={classes} />
        ) : (
          photos.map((imagem) => (
            <Grid key={imagem.uuid} item lg={3} md={4} sm={6} xs={6} className={classes.gridFotos}>
              <div className={clsx(classes.divFoto, isSelected(imagem) && classes.selecionada)}>
                {imagem.estado === 'carregando' ? (
                  <div className={classes.skeletonImagem} />
                ) : (
                  <>
                    <img
                      src={imagem.preview ?? imagem.src}
                      className={classes.imagem}
                      onClick={() => selectPhotoForDetail(imagem)}
                      onTouchStart={() => handleTouchStart(imagem)}
                      onTouchEnd={handleTouchEnd}
                      onTouchMove={handleTouchEnd}
                      onLoad={() => updatePhotoState(imagem)}
                      loading="lazy"
                      alt={imagem.nome || 'Foto da Galeria'}
                    />

                    {imagem.estado === 'metadado' && <div className={classes.skeletonImagem} />}

                    {!imagem.id && !imagem.uploaded && (
                      <div className={classes.uploadOverlay}>
                        <CircularProgress size={30} color="inherit" />
                        <span className={classes.uploadTexto}>Enviando...</span>
                      </div>
                    )}

                    {imagem.id && <div className={classes.fogOverlay} />}

                    <div className={classes.infoOverlay}>
                      <Typography className={classes.textoInfo}>
                        {imagem.usuarioUpload?.nome?.toUpperCase() ?? 'DESCONHECIDO'}
                      </Typography>
                      <Typography className={clsx(classes.textoInfo, classes.textoInfoData)}>
                        {imagem.data ? moment(imagem.data).format('DD/MM/YYYY HH:mm') : 'DESCONHECIDO'}
                      </Typography>
                    </div>

                    {imagem.id && (
                      <Checkbox
                        checked={isSelected(imagem)}
                        className={classes.checkOverlay}
                        icon={<RadioButtonUncheckedIcon />}
                        checkedIcon={<CheckCircleIcon />}
                        onChange={() => handleCheck(imagem)}
                      />
                    )}
                  </>
                )}
              </div>
            </Grid>
          ))
        )}
      </Grid>

      {/* Modal de Confirmação de Exclusão */}
      <ModalConfirmacao
        aberto={isDeleteModalOpen}
        onClose={() => !deleting && setIsDeleteModalOpen(false)}
        onConfirm={handleDelete}
        processando={deleting}
        titulo="EXCLUIR FOTOS DA GALERIA"
        textoConfirmar="Excluir"
        textoCancelar="Cancelar"
        confirmarColor="error"
        cancelarColor="primary"
      >
        <Typography variant="body1">
          Tem certeza que deseja excluir as <strong>{selectedPhotos.length}</strong> fotos selecionadas? Esta ação removerá os arquivos permanentemente e não poderá ser desfeita.
        </Typography>
      </ModalConfirmacao>

      {/* Modal de Visualização Detalhada (Lightbox com Zoom) */}
      <Modal
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        className={classes.modal}
        open={isDetailOpen}
        onClose={closePhotoDetail}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={isDetailOpen}>
          {detailPhoto ? (
            <div className={classes.divFotoDetalhada}>
              <img
                src={detailPhoto.preview ?? detailPhoto.src}
                title={detailPhoto.nome || detailPhoto.title || 'Visualização da Foto'}
                alt={detailPhoto.nome || detailPhoto.title || 'Visualização da Foto'}
                className={classes.fotoDetalhada}
                draggable="false"
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUpOrLeave}
                onMouseLeave={handleMouseUpOrLeave}
                onTouchStart={handleTouchStartDrag}
                onTouchMove={handleTouchMoveDrag}
                onTouchEnd={handleTouchEndDrag}
                style={{
                  transform: `translate(${panPosition.x}px, ${panPosition.y}px) scale(${zoom})`,
                  transformOrigin: 'center center',
                  transition: isDragging ? 'none' : 'transform 0.15s ease-out',
                  cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
                  userSelect: 'none',
                }}
              />
              <div className={classes.botoesImagemDetalhada}>
                <IconButton onClick={zoomIn} title="Aumentar Zoom">
                  <ZoomInIcon fontSize="large" />
                </IconButton>
                <IconButton onClick={zoomOut} title="Diminuir Zoom">
                  <ZoomOutIcon fontSize="large" />
                </IconButton>
                <IconButton onClick={handleDownload} title="Baixar Imagem">
                  <CloudDownload fontSize="large" />
                </IconButton>
              </div>
            </div>
          ) : (
            <div />
          )}
        </Fade>
      </Modal>
    </div>
  )
}

Galeria.propTypes = {
  importado: PropTypes.bool,
  tipoObjeto: PropTypes.string,
  idObjeto: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
}

